import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom'; // Import useNavigate
import {
  HomeIcon, DocumentPlusIcon, ClockIcon, DocumentTextIcon, CogIcon, RectangleStackIcon,
  BuildingOfficeIcon, BuildingOffice2Icon, BriefcaseIcon, ClipboardDocumentListIcon, AcademicCapIcon, UsersIcon,
  UserGroupIcon, ChevronDownIcon, ChevronUpIcon, ChartBarIcon, CalendarDaysIcon, MapIcon, // Added CalendarDaysIcon and MapIcon
  XMarkIcon, // Import XMarkIcon for close button
  ShieldCheckIcon, ArrowRightOnRectangleIcon, // Add logout icon
  BeakerIcon, // SDS Library icon
  FolderOpenIcon, // Resources Library icon
  ClipboardDocumentCheckIcon, // Connect Requests icon
  LinkIcon, // Client Connections icon
  MapPinIcon, // Where to Send a Worker icon
  QuestionMarkCircleIcon // Help icon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext'; // Import useAuth
import { supabase } from '../../supabaseClient'; // Import for logout functionality
import primaryLogo from '../../assets/logo/syncehs-lockup-black-v2.svg'; // Import the new logo
import iconLogo from '../../assets/logo/syncehs-icon-transparent.png'; // Compact logo for icon rail

// Case Management icon: a first aid kit, drawn to match the Heroicons outline set (24px grid, 1.5 stroke).
// Heroicons has no medical or first aid icon.
const FirstAidKitIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75V5.25A1.5 1.5 0 0 1 9.75 3.75h4.5a1.5 1.5 0 0 1 1.5 1.5v1.5M4.5 6.75h15a1.5 1.5 0 0 1 1.5 1.5v10.5a1.5 1.5 0 0 1-1.5 1.5h-15a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5ZM12 10.5v6m-3-3h6" />
  </svg>
);

// Define types for navigation items
interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

interface NavGroup {
  name: string;
  icon: React.ElementType;
  items: NavItem[];
}

type NavigationEntry = NavItem | NavGroup;

// Define props for Sidebar
interface SidebarProps {
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobileMenuOpen, toggleMobileMenu }) => {
  const location = useLocation();
  const navigate = useNavigate(); // Hook for navigation
  const { profile, loading: authLoading, isSuperAdmin, hasSafetyModules, isConnectClient } = useAuth(); // Get user profile and loading state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  // Base navigation structure (Keep this updated as needed)
  const navigation: NavigationEntry[] = [
    ...(hasSafetyModules ? [{ name: 'Dashboard', href: '/dashboard', icon: HomeIcon }] : []),
    ...(isSuperAdmin ? [
      { name: 'Super Admin', href: '/super-admin', icon: ShieldCheckIcon },
      { name: 'Connect Management', href: '/admin/connect-management', icon: ClipboardDocumentCheckIcon },
    ] : []),
    ...(hasSafetyModules ? [
      {
        name: 'Incidents', icon: RectangleStackIcon, items: [
          { name: 'View Incidents', href: '/view-incidents', icon: RectangleStackIcon },
          { name: 'Add Incident', href: '/add-incident', icon: DocumentPlusIcon },
        ]
      },
      {
        name: 'Case Management', icon: FirstAidKitIcon, items: [
          { name: 'Where to Send a Worker', href: '/case-management/where-to-send', icon: MapPinIcon },
          { name: 'Designated Providers', href: '/case-management/providers', icon: BuildingOffice2Icon },
          { name: 'Physical Demands', href: '/case-management/physical-demands', icon: ClipboardDocumentCheckIcon },
        ]
      },
      { name: 'Indicators Hub', href: '/leading-indicators', icon: ClipboardDocumentListIcon },
      { name: 'Employees', href: '/manage-employees', icon: UserGroupIcon },
      {
        name: 'Training', icon: AcademicCapIcon, items: [
          { name: 'Training Status', href: '/manage-employee-training', icon: UserGroupIcon },
          { name: 'Training Courses', href: '/manage-trainings', icon: AcademicCapIcon },
          { name: 'Training Assignments', href: '/training-requirements', icon: ClipboardDocumentListIcon },
          { name: 'Training Compliance', href: '/reports/training', icon: ChartBarIcon },
        ]
      },
      {
        name: 'Safety Meetings', icon: CalendarDaysIcon, items: [
          { name: 'View Meetings', href: '/safety-meetings', icon: ClipboardDocumentListIcon },
          { name: 'Add Meeting', href: '/safety-meetings/add', icon: DocumentPlusIcon },
        ]
      },
      {
        name: 'Location Data', icon: MapIcon, items: [
          { name: 'Manage Locations', href: '/manage-locations', icon: BuildingOfficeIcon },
          { name: 'Manage Projects', href: '/manage-projects', icon: BriefcaseIcon },
          { name: 'Manage Hours', href: '/manage-hours', icon: ClockIcon },
        ]
      },
      {
        name: 'Compliance', icon: DocumentTextIcon, items: [
          { name: 'OSHA Logs', href: '/osha-logs', icon: DocumentTextIcon },
          { name: 'Corrective Actions', href: '/manage-corrective-actions', icon: ClipboardDocumentListIcon },
        ]
      },
      { name: 'SDS Library', href: '/sds-library', icon: BeakerIcon },
      { name: 'Resources Library', href: '/resources-library', icon: FolderOpenIcon },
      { name: 'Reporting Hub', href: '/reports', icon: ChartBarIcon },
    ] : []),
    ...(hasSafetyModules ? [{ name: 'Company Documents', href: '/company-documents', icon: DocumentTextIcon }] : []),
    ...(hasSafetyModules ? [{ name: 'Client Connections', href: '/my-connections', icon: LinkIcon }] : []),
    ...(isConnectClient ? [{ name: 'Sub Connections', href: '/connected-orgs', icon: BuildingOffice2Icon }] : []),
    { name: 'Settings', href: '/settings', icon: CogIcon },
    { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon },
  ];

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(entry => {
    const adminOnly = ['Location Data', 'Settings', 'Training', 'Employees', 'Resources Library'];
    if (adminOnly.includes(entry.name)) {
     if (!authLoading && profile && profile.role !== 'ORG_ADMIN' && profile.role !== 'SUPER_ADMIN') {
  return false;
}
      return true;
    }
    return true;
  });

  const isActive = (href: string) => location.pathname === href;
  const isSectionActive = (section: NavGroup) => section.items.some(item => isActive(item.href));

  const toggleSection = (sectionName: string) => {
    setOpenSections(prevOpenSections => {
      const isCurrentlyOpen = !!prevOpenSections[sectionName];
      const newState: Record<string, boolean> = {};
      if (!isCurrentlyOpen) {
        newState[sectionName] = true;
      }
      return newState;
    });
  };

  // Function to handle navigation and close mobile menu
  const handleNavigate = (href: string) => {
    navigate(href);
    if (isMobileMenuOpen) {
      toggleMobileMenu(); // Close menu on navigation
    }
  };

  // Function to handle logout for mobile
  const handleLogout = async () => {
    await supabase.auth.signOut({ scope: 'local' });
    if (isMobileMenuOpen) {
      toggleMobileMenu(); // Close menu on logout
    }
  };

  // Initialize open sections based on active route
  useState(() => {
    const activeSection = filteredNavigation.find(entry => 'items' in entry && isSectionActive(entry as NavGroup)) as NavGroup | undefined;
    if (activeSection) {
      setOpenSections({ [activeSection.name]: true });
    }
  });

  const linkClasses = (active: boolean) => `
    group flex items-center px-4 py-3 text-sm font-medium rounded-lg font-sans transition-colors duration-150
    ${active ? 'bg-accent-blue/20 text-white border-l-2 border-accent-blue' : 'text-white/70 hover:bg-surface-card/10 hover:text-white'}
  `;

  const iconClasses = (active: boolean) => `
    mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-150
    ${active ? 'text-white' : 'text-white/50 group-hover:text-white'}
  `;

  const subLinkClasses = (active: boolean) => `
    group flex items-center pl-11 pr-2 py-3 text-sm font-medium rounded-lg font-sans transition-colors duration-150
    ${active ? 'bg-accent-blue/20 text-white' : 'text-white/70 hover:bg-surface-card/10 hover:text-white'}
  `;

  // Helper function to render navigation items
  const renderNavItems = (isMobile: boolean) => (
    authLoading ? (
      <div className="p-4 text-white/50">Loading navigation...</div>
    ) : (
      filteredNavigation.map((entry) => {
        if ('items' in entry) {
          const group = entry as NavGroup;
          const isOpen = openSections[group.name] || false;
          const sectionActive = isSectionActive(group);
          return (
            <div key={group.name}>
              <button
                onClick={() => toggleSection(group.name)}
                className={`${linkClasses(sectionActive && !isOpen)} w-full text-left`}
                aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${group.name} section`}
              >
                <group.icon className={iconClasses(sectionActive && !isOpen)} aria-hidden="true" />
                <span className="flex-1">{group.name}</span>
                {isOpen ? (
                  <ChevronUpIcon className="ml-auto h-5 w-5 transform group-hover:text-white transition-transform duration-150" aria-hidden="true" />
                ) : (
                  <ChevronDownIcon className="ml-auto h-5 w-5 transform group-hover:text-white transition-transform duration-150" aria-hidden="true" />
                )}
              </button>
              {isOpen && (
                <div className="mt-1 space-y-1">
                  {group.items.map((item) => (
                    isMobile ? (
                      <button
                        key={item.name}
                        onClick={() => handleNavigate(item.href)}
                        className={`${subLinkClasses(isActive(item.href))} w-full text-left`}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <Link key={item.name} to={item.href} className={subLinkClasses(isActive(item.href))}>
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
          );
        } else {
          const item = entry as NavItem;
          return (
            isMobile ? (
              <button
                key={item.name}
                onClick={() => handleNavigate(item.href)}
                className={`${linkClasses(isActive(item.href))} w-full text-left`}
              >
                <item.icon className={iconClasses(isActive(item.href))} aria-hidden="true" />
                {item.name}
              </button>
            ) : (
              <Link key={item.name} to={item.href} className={linkClasses(isActive(item.href))}>
                <item.icon className={iconClasses(isActive(item.href))} aria-hidden="true" />
                {item.name}
              </Link>
            )
          );
        }
      })
    )
  );

  const SidebarContent = ({ isMobile }: { isMobile: boolean }) => (
    <>
      {/* Logo or Branding */}
      <div className="flex items-center justify-center h-16 flex-shrink-0 px-4 bg-surface-card"> {/* Changed background to white */}
        <img
          className="h-auto w-48 max-w-full"
          src={primaryLogo}
          alt="SyncEHS"
        />
      </div>
      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {renderNavItems(isMobile)}
        {/* Add Logout button for mobile only */}
        {isMobile && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <button
              onClick={handleLogout}
              className={`${linkClasses(false)} w-full text-left`}
            >
              <ArrowRightOnRectangleIcon className={iconClasses(false)} aria-hidden="true" />
              Logout
            </button>
          </div>
        )}
      </nav>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar (Hidden on small screens) */}
      <div className="hidden xl:fixed xl:inset-y-0 xl:left-0 xl:w-64 xl:bg-navy xl:text-white xl:flex xl:flex-col">
        <SidebarContent isMobile={false} />
      </div>

      {/* Icon-only Rail Sidebar (Tablet: 768px–1199px) */}
      <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-30 md:w-16 md:flex md:flex-col xl:hidden bg-navy text-white">
        {/* Compact Logo */}
        <div className="flex items-center justify-center h-16 flex-shrink-0 bg-surface-card">
          <img src={iconLogo} alt="SyncEHS" className="h-8 w-8 rounded" />
        </div>
        {/* Nav Icons */}
        <nav className="flex-1 overflow-y-auto py-4 space-y-1 px-2">
          {!authLoading && filteredNavigation.map((entry) => {
            if ('items' in entry) {
              const group = entry as NavGroup;
              const defaultHref = group.items[0]?.href || '/';
              const active = isSectionActive(group);
              return (
                <Link
                  key={group.name}
                  to={defaultHref}
                  className={`relative group flex items-center justify-center w-full py-3 rounded-lg transition-colors duration-150 ${
                    active
                      ? 'bg-accent-blue/20 border-l-2 border-accent-blue text-white'
                      : 'text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <group.icon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {group.name}
                  </div>
                </Link>
              );
            } else {
              const item = entry as NavItem;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative group flex items-center justify-center w-full py-3 rounded-lg transition-colors duration-150 ${
                    active
                      ? 'bg-accent-blue/20 border-l-2 border-accent-blue text-white'
                      : 'text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {item.name}
                  </div>
                </Link>
              );
            }
          })}
        </nav>
        {/* Logout */}
        <div className="flex-shrink-0 py-4 border-t border-white/20 px-2">
          <button
            onClick={handleLogout}
            className="relative group flex items-center justify-center w-full py-3 rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-colors duration-150"
          >
            <ArrowRightOnRectangleIcon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Sign Out
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Sidebar (Shown on small screens) */}
      <div className={`fixed inset-0 z-40 flex md:hidden ${isMobileMenuOpen ? '' : 'pointer-events-none'}`}>
        {/* Overlay */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={toggleMobileMenu}
          aria-hidden="true"
        ></div>

        {/* Sidebar Panel */}
        <div
          className={`relative flex-1 flex flex-col max-w-xs w-full bg-navy text-white transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          {/* Close button */}
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className={`ml-1 flex items-center justify-center h-11 w-11 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white ${isMobileMenuOpen ? '' : 'hidden'}`}
              onClick={toggleMobileMenu}
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </button>
          </div>
          <SidebarContent isMobile={true} />
        </div>
        {/* Dummy element to force sidebar to shrink to fit close icon */}
        <div className="flex-shrink-0 w-14" aria-hidden="true"></div>
      </div>
    </>
  );
};

export default Sidebar;

export {}; // For isolatedModules
